import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { StaffLineBindingDto } from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { MemberPortalControls, type LineActivity, type PortalWork } from './MemberPortalControls';
import { MemberLineRebindControls } from './MemberLineRebindControls';
import { MemberLineUnlinkControls } from './MemberLineUnlinkControls';

type Work = PortalWork | 'rebind' | 'direct';

export function MemberLinePanel({ memberId }: { memberId: string }) {
  const actions = useAuthStore((s) => s.allowedActions);
  const session = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const actor = useAuthStore((s) => s.user?.id);
  if (
    import.meta.env.VITE_MEMBER_PILOT_ENABLED !== 'true' ||
    !actions?.some((a) => ['member:read', 'member:portal_manage', 'member:line_rebind'].includes(a))
  )
    return null;
  // Unmount sensitive state synchronously at every identity/authority boundary.
  return (
    <LinePanel
      key={JSON.stringify([memberId, session, venue, actor, [...actions].sort()])}
      memberId={memberId}
      actions={actions}
    />
  );
}

function LinePanel({ memberId, actions }: { memberId: string; actions: string[] }) {
  const { t } = useTranslation('member');
  const canRead = actions.includes('member:read');
  const canManage = actions.includes('member:portal_manage');
  const [binding, setBinding] = useState<StaffLineBindingDto | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [cases, setCases] = useState<number | null>(null);
  const [casesError, setCasesError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [work, setWork] = useState<Work | null>(null);
  const [activity, setActivity] = useState<LineActivity>({ busy: false, dirty: false });
  const opener = useRef<HTMLButtonElement>(null);
  const changed = useCallback(() => setRevision((n) => n + 1), []);
  useEffect(() => {
    let current = true;
    setBinding(null);
    setStatus('loading');
    if (canRead)
      void api
        .get<StaffLineBindingDto>(`/members/${memberId}/line-binding`)
        .then((value) => {
          if (current) {
            setBinding(value);
            setStatus('ready');
          }
        })
        .catch(() => {
          if (current) setStatus('error');
        });
    if (canManage) {
      setCases(null);
      setCasesError(false);
      void api
        .get<unknown[]>('/members/registration-cases/pending')
        .then((value) => {
          if (current) setCases(value.length);
        })
        .catch(() => {
          if (current) setCasesError(true);
        });
    }
    return () => {
      current = false;
    };
  }, [memberId, canRead, canManage, revision]);
  function discard() {
    return !activity.busy && (!activity.dirty || window.confirm(t('linePanel.discard')));
  }
  function close() {
    if (!discard()) return;
    setOpen(false);
    setWork(null);
    setActivity({ busy: false, dirty: false });
  }
  function select(next: Work | null) {
    if (!discard()) return;
    setWork(next);
    setActivity({ busy: false, dirty: false });
  }
  const dialog = useDialogFocus(open, close, activity.busy);
  useEffect(() => {
    if (open) dialog.current?.focus({ preventScroll: true });
  }, [work, open, dialog]);
  const bindingLabel = !canRead
    ? t('linePanel.noStatus')
    : status === 'loading'
      ? t('linePanel.loading')
      : status === 'error'
        ? t('linePanel.error')
        : !binding?.enabled
          ? t('linePanel.disabled')
          : !binding.bound
            ? t('linePanel.unbound')
            : binding.status === 'ACTIVE'
              ? t('linePanel.active')
              : binding.status === 'REAUTH_REQUIRED'
                ? t('linePanel.reauth')
                : binding.status === 'SUSPENDED'
                  ? t('linePanel.suspended')
                  : t('linePanel.unknown');
  const available: Work[] = [
    ...(canManage ? (['link', 'unlink'] as Work[]) : []),
    ...(actions.includes('member:line_rebind') ? (['rebind'] as Work[]) : []),
    ...(canRead ? (['scan'] as Work[]) : []),
  ];
  const danger: Work[] = [
    ...(canRead && actions.includes('member:line_unlink') ? (['direct'] as Work[]) : []),
    ...(canManage ? (['suspend'] as Work[]) : []),
  ];
  function choice(kind: Work) {
    return (
      <button
        key={kind}
        type="button"
        disabled={
          activity.busy ||
          (!['scan', 'cases'].includes(kind) &&
            canRead &&
            (status !== 'ready' || !binding?.enabled))
        }
        className={`w-full rounded border border-base-300 p-3 text-left hover:bg-base-200 ${danger.includes(kind) ? 'text-error' : ''}`}
        onClick={() => select(kind)}
      >
        <span className="block font-semibold">{t(`linePanel.${kind}`)}</span>
        <span className="block text-sm text-base-content/70">{t(`linePanel.${kind}Help`)}</span>
      </button>
    );
  }
  return (
    <section
      aria-label={t('linePanel.binding')}
      className="mb-4 rounded-lg border border-base-300 bg-base-100 p-4 [overflow-wrap:anywhere]"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold">{t('linePanel.binding')}</h2>
          <p role="status" className="text-sm">
            {bindingLabel}
          </p>
        </div>
        <button
          type="button"
          ref={opener}
          aria-expanded={open}
          aria-controls="member-line-management"
          onClick={() => setOpen(true)}
          className="min-h-11 rounded border border-base-300 px-4"
        >
          {t('linePanel.manage')}
        </button>
      </div>
      {canManage && (
        <p className="mt-2 text-sm" role="status">
          {casesError
            ? t('linePanel.venueError')
            : cases === null
              ? t('linePanel.venueLoading')
              : t('linePanel.venueCount', { count: cases })}
        </p>
      )}
      {open && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/40">
          <div
            ref={dialog}
            id="member-line-management"
            role="dialog"
            aria-modal="true"
            aria-labelledby="member-line-title"
            tabIndex={-1}
            className="h-dvh w-full max-w-md overflow-y-auto bg-base-100 p-4 shadow-xl [overflow-wrap:anywhere]"
          >
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-base-300 pb-3">
              <h2 id="member-line-title" className="text-lg font-semibold">
                {t('linePanel.management')}
              </h2>
              <button
                type="button"
                disabled={activity.busy}
                className="min-h-11 px-3"
                onClick={close}
              >
                {t('linePanel.close')}
              </button>
            </div>
            <p className="mb-4 text-sm">{bindingLabel}</p>
            {(status === 'error' || casesError) && (
              <button
                type="button"
                disabled={activity.busy}
                className="mb-3 min-h-11 underline"
                onClick={changed}
              >
                {t('linePanel.retry')}
              </button>
            )}
            {work ? (
              <>
                <button
                  type="button"
                  disabled={activity.busy}
                  onClick={() => select(null)}
                  className="mb-3 min-h-11 underline"
                >
                  {t('linePanel.back')}
                </button>
                <h3 className="mb-1 font-semibold">{t(`linePanel.${work}`)}</h3>
                <p className="mb-4 text-sm">{t(`linePanel.${work}Help`)}</p>
                {work === 'rebind' ? (
                  <MemberLineRebindControls
                    memberId={memberId}
                    onActivity={setActivity}
                    onChanged={changed}
                  />
                ) : work === 'direct' ? (
                  <MemberLineUnlinkControls
                    memberId={memberId}
                    onActivity={setActivity}
                    onChanged={changed}
                  />
                ) : (
                  <MemberPortalControls
                    key={work}
                    memberId={memberId}
                    work={work}
                    onActivity={setActivity}
                    onChanged={changed}
                  />
                )}
              </>
            ) : (
              <>
                <p className="mb-3">{t('linePanel.choose')}</p>
                <div className="space-y-3">{available.map(choice)}</div>
                {danger.length > 0 && (
                  <div className="mt-6 space-y-3 border-t border-base-300 pt-3">
                    <h3>{t('linePanel.danger')}</h3>
                    {danger.map(choice)}
                  </div>
                )}
                {canManage && (
                  <div className="mt-6 border-t border-base-300 pt-3">{choice('cases')}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
